import { createViewDay, createViewWeek } from "@schedule-x/calendar";
import { createCalendarControlsPlugin } from "@schedule-x/calendar-controls";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import { ScheduleXCalendar, useCalendarApp } from "@schedule-x/react";
import "@schedule-x/theme-default/dist/index.css";
// A lib espera Temporal global; o import nomeado dá os tipos pra gente.
import "temporal-polyfill/global";

import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Temporal } from "temporal-polyfill";
import { appointmentsQuery } from "@/api/appointments";
import { businessHoursQuery, establishmentQuery } from "@/api/establishment";
import { professionalsQuery } from "@/api/professionals";
import { scheduleExceptionsQuery } from "@/api/schedule-exceptions";
import { waitlistQuery } from "@/api/waitlist";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import { AgendamentoEventoContent } from "@/components/AgendamentoEventoModal";
import { SlotHover } from "@/components/agenda/SlotHover";
import { calcularDemandaPorHora, picoDe } from "@/components/appointments/demanda";
import { TermometroDemanda } from "@/components/appointments/TermometroDemanda";
import { TermometroVertical } from "@/components/appointments/TermometroVertical";
import { NovoAgendamentoDialog } from "@/components/NovoAgendamentoDialog";
import { Button } from "@/components/ui/button";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/ui/responsive-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { todayInZone } from "@/lib/datetime";
import {
  datasNoIntervalo,
  dentroDoExpediente,
  faixasFechadas,
  paraHora,
  paraMinutos,
} from "@/lib/expediente";
import { useTheme } from "@/lib/theme";

export const Route = createFileRoute("/_authed/agenda")({
  // Só o que a tela precisa pra DESENHAR a grade. Os agendamentos ficam de
  // fora de propósito: a janela depende da data e da visão que o componente
  // escolhe, e prefetchar a semana errada seria uma requisição jogada fora.
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [
      establishmentQuery(establishmentId),
      businessHoursQuery(establishmentId),
      professionalsQuery(establishmentId),
      // Feriado é tão "não dá pra agendar" quanto domingo, e sem isto a
      // grade pintaria uma quarta-feira de Natal como dia normal.
      scheduleExceptionsQuery(establishmentId),
    ]),
  staticData: { title: "Agenda" },
  component: AgendaPage,
});

/**
 * A grade responde DUAS perguntas ao mesmo tempo: de quem é o horário e em
 * que estado ele está. Antes ela só respondia a segunda, e mal: "confirmado"
 * usava `--primary`, então o evento MAIS COMUM da agenda era o mais chapado
 * da tela e todos os profissionais tinham a mesma cara.
 *
 * Agora o preenchimento identifica a PESSOA e a barra lateral identifica o
 * ESTADO. O normal (confirmado) fica calmo, na cor de quem atende; só o que
 * pede ação — aguardando, falta — ganha barra em cor de alerta.
 *
 * A paleta é de SLOTS fixos, não uma cor por profissional cadastrado: o
 * Schedule-X recebe `calendars` na inicialização e não expõe troca depois, e
 * profissional é criado em tempo de execução. Cada um cai num slot pelo hash
 * do próprio id — estável entre recargas e entre máquinas, que é o que
 * importa pra memória visual de quem usa a tela todo dia.
 */
const PROFESSIONAL_HUES = [
  "var(--color-sky-500)",
  "var(--color-violet-500)",
  "var(--color-emerald-500)",
  "var(--color-orange-500)",
  "var(--color-pink-500)",
  "var(--color-teal-500)",
  "var(--color-indigo-500)",
  "var(--color-lime-600)",
] as const;

/** Hash estável (djb2) — mesmo id, mesmo slot, sempre. */
function hueIndexFor(professionalId: string): number {
  let hash = 5381;
  for (let i = 0; i < professionalId.length; i++) {
    hash = (hash * 33) ^ professionalId.charCodeAt(i);
  }
  return Math.abs(hash) % PROFESSIONAL_HUES.length;
}

/**
 * A barra lateral por estado. `confirmed` usa a própria cor do profissional
 * de propósito: estado normal não precisa de destaque, precisa de calma.
 */
const STATUS_BAR = {
  confirmed: null, // usa a cor do profissional
  awaiting: "var(--color-amber-500)",
  completed: "var(--border)",
  noShow: "var(--destructive)",
} as const;

type StatusKey = keyof typeof STATUS_BAR;

interface SxCalendarColors {
  main: string;
  container: string;
  onContainer: string;
}

interface SxCalendar {
  colorName: string;
  lightColors: SxCalendarColors;
  darkColors: SxCalendarColors;
}

function buildCalendars(): Record<string, SxCalendar> {
  const calendars: Record<string, SxCalendar> = {};
  PROFESSIONAL_HUES.forEach((hue, index) => {
    for (const status of Object.keys(STATUS_BAR) as StatusKey[]) {
      const bar = STATUS_BAR[status] ?? hue;
      // Concluído esvazia: já aconteceu, não disputa mais atenção.
      const container =
        status === "completed"
          ? "var(--muted)"
          : `color-mix(in oklab, ${hue} 16%, var(--card))`;
      const onContainer =
        status === "completed" ? "var(--muted-foreground)" : "var(--foreground)";
      const colors: SxCalendarColors = { main: bar, container, onContainer };
      calendars[`p${index}-${status}`] = {
        colorName: `p${index}-${status}`,
        lightColors: colors,
        darkColors: colors,
      };
    }
  });
  return calendars;
}

const SX_CALENDARS = buildCalendars();

const SX_STATUS_KEY: Record<string, StatusKey> = {
  confirmed: "confirmed",
  awaiting_confirmation: "awaiting",
  completed: "completed",
  no_show: "noShow",
};

/** Cancelado não vira evento: o slot já foi liberado e exibi-lo confunde. */
function calendarIdFor(professionalId: string, status: string): string {
  const statusKey = SX_STATUS_KEY[status] ?? "awaiting";
  return `p${hueIndexFor(professionalId)}-${statusKey}`;
}

const viewWeek = createViewWeek();
const viewDay = createViewDay();

function AgendaPage() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Suspense fallback={<AgendaSkeleton />}>
      <AgendaCalendar establishmentId={user.establishment_id} />
    </Suspense>
  );
}

function AgendaSkeleton() {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-card px-6 py-3">
        <Skeleton className="h-8 w-72" />
      </div>
      <div className="flex-1 p-6">
        <Skeleton className="h-full w-full" />
      </div>
    </div>
  );
}

function AgendaCalendar({ establishmentId }: { establishmentId: string }) {
  const { data: establishment } = useSuspenseQuery(establishmentQuery(establishmentId));
  const { data: businessHours } = useSuspenseQuery(businessHoursQuery(establishmentId));
  const { data: scheduleExceptions } = useSuspenseQuery(
    scheduleExceptionsQuery(establishmentId),
  );
  const { data: professionals } = useSuspenseQuery(professionalsQuery(establishmentId));
  const timeZone = establishment.timezone;
  const today = todayInZone(timeZone);

  // Plugins precisam ser estáveis entre renders (a config do useCalendarApp
  // só vale na criação).
  const [eventsService] = useState(() => createEventsServicePlugin());
  const [calendarControls] = useState(() => createCalendarControlsPlugin());
  // Agendamento aberto no modal, por id. O popover do plugin `eventModal` do
  // Schedule-X foi trocado por um Dialog nosso: ele nascia ancorado no evento,
  // sem rolagem, e num horário da tarde as ações caíam fora da tela.
  const [openId, setOpenId] = useState<string | null>(null);

  const [view, setView] = useState<"week" | "day">(() =>
    typeof window !== "undefined" && window.innerWidth < 1024 ? "day" : "week",
  );
  // Janela visível dirigida pelo calendário (onRangeUpdate) — é ela que
  // define o date_range da query. Nasce com a semana/dia de verdade (não
  // null): começar em "hoje..hoje" e corrigir quando o calendário reporta a
  // janela real causava um refetch na montagem — uma das "piscadas".
  const [range, setRange] = useState(() => {
    if (view === "day") return { start: today, end: today };
    const t = Temporal.PlainDate.from(today);
    const monday = t.subtract({ days: (t.dayOfWeek + 6) % 7 });
    return { start: monday.toString(), end: monday.add({ days: 6 }).toString() };
  });
  // Clique num horário vazio da grade → abre o NovoAgendamentoDialog já com
  // data/hora daquele ponto (o slot livre mais próximo é pré-selecionado lá).
  const [slotPrefill, setSlotPrefill] = useState<{
    date: string;
    time: string;
  } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // Filtro de profissional da toolbar. "all" é sentinela porque o Select do
  // Radix não aceita value="" em SelectItem.
  const [professionalFilter, setProfessionalFilter] = useState<string>("all");

  const dayBoundaries = useMemo(() => {
    const own = businessHours.filter((h) => !h.professional_id);
    if (own.length === 0) return { start: "08:00", end: "20:00" };
    // dayBoundaries só aceita hora cheia — piso na abertura, teto no fecha.
    const start = Math.min(...own.map((h) => Number(h.opens_at.split(":")[0] ?? 8)));
    const end = Math.max(
      ...own.map((h) => {
        const [hh, mm] = h.closes_at.split(":").map(Number);
        return (mm ?? 0) > 0 ? (hh ?? 20) + 1 : (hh ?? 20);
      }),
    );
    const pad = (n: number) => `${String(Math.min(n, 24)).padStart(2, "0")}:00`;
    return { start: pad(start), end: pad(end) };
  }, [businessHours]);

  const { isDark } = useTheme();

  /**
   * As faixas fora do expediente, desenhadas como fundo da grade.
   *
   * A grade é recortada pela MAIOR janela da semana (`dayBoundaries`), então
   * um sábado que fecha às 14h e um domingo fechado ocupavam a mesma altura
   * de uma terça inteira, com a mesma aparência de espaço livre. O dono via
   * vaga onde não há atendimento nenhum.
   *
   * Calculado UMA vez, pra uma janela larga de datas, porque o
   * `useCalendarApp` monta o calendário no primeiro render e nunca mais lê a
   * config — não há como acrescentar faixa ao navegar. Um ano pra frente
   * cobre qualquer navegação real de uma barbearia; além disso a faixa
   * some, e aí a grade volta a mentir. Se um dia alguém navegar tão longe, o
   * conserto é recriar o calendário, não esticar mais a janela.
   */
  const backgroundEvents = useMemo(() => {
    const hoje = Temporal.Now.plainDateISO(timeZone);
    const de = hoje.subtract({ days: 30 }).toString();
    const ate = hoje.add({ days: 365 }).toString();
    const janela = {
      inicio: paraMinutos(dayBoundaries.start),
      fim: paraMinutos(dayBoundaries.end),
    };

    return faixasFechadas(
      datasNoIntervalo(de, ate),
      businessHours,
      scheduleExceptions,
      janela,
    ).map((faixa) => ({
      start: Temporal.PlainDateTime.from(
        `${faixa.date}T${paraHora(faixa.inicio)}`,
      ).toZonedDateTime(timeZone),
      end: Temporal.PlainDateTime.from(
        `${faixa.date}T${paraHora(faixa.fim)}`,
      ).toZonedDateTime(timeZone),
      // A cor sai de um token do painel via CSS (ver styles.css): aqui só
      // marcamos a faixa. `style` é obrigatório no tipo da lib.
      style: {},
    }));
  }, [businessHours, scheduleExceptions, dayBoundaries, timeZone]);

  const calendar = useCalendarApp({
    views: [viewWeek, viewDay],
    defaultView: view,
    locale: "pt-BR",
    timezone: timeZone,
    firstDayOfWeek: 1, // segunda
    // Vem do tema do PAINEL, não de `prefers-color-scheme`: com o seletor
    // Claro/Escuro/Sistema, ler o SO direto deixaria o calendário escuro
    // dentro de um painel claro sempre que a escolha divergisse do sistema.
    isDark,
    dayBoundaries,
    // A transição deslizante entre períodos somada à chegada assíncrona dos
    // eventos dava sensação de "piscada" a cada navegação/recarga.
    skipAnimations: true,
    weekOptions: {
      gridHeight: 1400,
      // Grade de 30min: os serviços costumam ter duração de 30/45min.
      gridStep: 30,
    },
    calendars: SX_CALENDARS,
    backgroundEvents,
    plugins: [eventsService, calendarControls],
    callbacks: {
      onClickDateTime(dateTime) {
        const date = dateTime.toPlainDate().toString();
        const hora = dateTime.toPlainTime().toString().slice(0, 5);
        // Clique fora do expediente não abre o formulário. Antes abria, e a
        // pessoa preenchia cliente e serviço pra só então a API recusar o
        // horário — o trabalho todo jogado fora depois de feito.
        if (
          !dentroDoExpediente(
            date,
            paraMinutos(hora),
            businessHours,
            scheduleExceptions,
          )
        ) {
          return;
        }
        setSlotPrefill({ date, time: hora });
        setCreateOpen(true);
      },
      onEventClick(calendarEvent) {
        setOpenId(String(calendarEvent.id));
      },
      onRangeUpdate(newRange) {
        setRange({
          start: String(newRange.start).slice(0, 10),
          end: String(newRange.end).slice(0, 10),
        });
      },
    },
  });

  const dateRange = `${range.start}..${range.end}`;
  const appointmentsReq = useQuery({
    // O filtro entra na query key (["appointments", filter]) — trocar de
    // profissional refaz a busca e a invalidação por prefixo continua valendo.
    ...appointmentsQuery({
      dateRange,
      professionalId: professionalFilter === "all" ? undefined : professionalFilter,
    }),
    // Navegação de semana mantém a grade anterior no lugar enquanto carrega.
    placeholderData: keepPreviousData,
  });
  const appointments = useMemo(
    () => (appointmentsReq.data ?? []).filter((a) => a.status !== "cancelled"),
    [appointmentsReq.data],
  );

  // O cliente vem RESOLVIDO dentro do agendamento (`a.customer`). Antes
  // vinha só o customer_id e a tela buscava um a um — uma requisição HTTP
  // por cliente da semana, que o Sentry flagrou como "N+1 API Call" em
  // /agenda. O mapa continua existindo porque o diálogo de detalhe procura
  // por id, e porque o mesmo cliente se repete na grade.
  const customerNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of appointments) {
      if (a.customer?.name) map.set(a.customer_id, a.customer.name);
    }
    return map;
  }, [appointments]);

  // Espelha a query no events-service do calendário. Agora basta esperar os
  // agendamentos: o nome chega junto deles, então não há mais a sequência de
  // resoluções que regravava os eventos e piscava um nome por vez.
  useEffect(() => {
    if (appointmentsReq.isPending) return;
    eventsService.set(
      appointments.map((a) => ({
        id: a.id,
        title: `${a.duration_override_minutes != null ? "Encaixe · " : ""}${a.booked_for || customerNames.get(a.customer_id) || "…"} — ${a.service_name}`,
        // A API devolve instante ISO ("...Z"): ZonedDateTime.from() exige
        // anotação de fuso no próprio string, então a conversão é via
        // Instant + fuso do estabelecimento.
        start: Temporal.Instant.from(a.starts_at).toZonedDateTimeISO(timeZone),
        end: Temporal.Instant.from(a.ends_at).toZonedDateTimeISO(timeZone),
        calendarId: calendarIdFor(a.professional_id, a.status),
      })),
    );
  }, [appointments, customerNames, appointmentsReq.isPending, eventsService, timeZone]);

  // Fila de espera do estabelecimento inteira, sem filtro de profissional na
  // API: a entrada SEM profissional definido ("qualquer um serve") sumiria do
  // resultado filtrado, e é justamente ela que pressiona todo mundo. Quem
  // recorta pelo escopo é o termômetro.
  const waitlistReq = useQuery(waitlistQuery(establishmentId));

  // Escopo do termômetro: o profissional filtrado, ou todos os que estão
  // atendendo. Inativo não entra na capacidade — ele não recebe agendamento
  // novo, então contar o expediente dele diluiria a ocupação de quem atende.
  const scopedProfessionals = useMemo(() => {
    const ativos = (professionals ?? []).filter((p) => p.active);
    return professionalFilter === "all"
      ? ativos
      : ativos.filter((p) => p.id === professionalFilter);
  }, [professionals, professionalFilter]);

  const professionalNames = useMemo(
    () => new Map((professionals ?? []).map((p) => [p.id, p.name] as const)),
    [professionals],
  );

  // O agendamento aberto, resolvido da lista já carregada. Vira `null` sozinho
  // quando a ação some com ele da grade (cancelar, remarcar pra outro dia) —
  // e aí o Dialog fecha, em vez de ficar mostrando um dado que não existe mais
  // naquela janela.
  const openAppointment = appointments.find((a) => a.id === openId) ?? null;

  // Largura da calha do eixo de horas, MEDIDA na grade renderizada em vez de
  // chutada: é ela que faz o termômetro cair exatamente sobre as colunas dos
  // dias. Não existe custom property da lib pra isso, e o valor muda com a
  // largura da tela — daí o ResizeObserver em vez de uma constante.
  const gradeRef = useRef<HTMLDivElement>(null);
  const [calhaPx, setCalhaPx] = useState(0);
  // O eixo de horas é onde a régua vertical é plantada por portal: é o elemento
  // que rola junto com a grade e tem exatamente a altura da janela de horas.
  const [eixoHoras, setEixoHoras] = useState<HTMLElement | null>(null);
  // `view` e `range` não são lidos dentro do efeito: são o GATILHO. Trocar de
  // visão ou de período faz o Schedule-X recriar os nós da grade, e a medida
  // antiga passa a valer pra um DOM que não existe mais.
  // biome-ignore lint/correctness/useExhaustiveDependencies: gatilho de remedida, não leitura
  useEffect(() => {
    const grade = gradeRef.current;
    if (!grade) return;

    const medir = () => {
      const primeiroDia = grade.querySelector<HTMLElement>(".sx__time-grid-day");
      if (primeiroDia) {
        const largura =
          primeiroDia.getBoundingClientRect().left - grade.getBoundingClientRect().left;
        setCalhaPx((atual) =>
          Math.abs(atual - largura) < 0.5 ? atual : Math.max(0, largura),
        );
      }
      const eixo = grade.querySelector<HTMLElement>(".sx__week-grid__time-axis");
      setEixoHoras((atual) => (atual === eixo ? atual : eixo));
    };

    // A grade é montada pelo Schedule-X DEPOIS deste efeito, e trocar de
    // visão/período troca os nós — sempre um instante depois do render do
    // React. Por isso a medida insiste por meio segundo em vez de parar na
    // primeira que dá certo: ao trocar pra visão de dia, a primeira tentativa
    // ainda encontra o eixo ANTIGO, e parar ali deixava a régua vertical
    // plantada num nó que já saiu da tela.
    let tentativas = 0;
    let timer: number | undefined;
    const tentar = () => {
      medir();
      if (tentativas++ > 6) return;
      timer = window.setTimeout(tentar, 120);
    };
    tentar();

    const observador = new ResizeObserver(medir);
    observador.observe(grade);
    return () => {
      observador.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [view, range]);

  // Janela de horas da régua vertical: a MESMA da grade (dayBoundaries), senão
  // as faixas cairiam deslocadas das linhas de hora.
  const [abreHora, fechaHora] = useMemo(
    () => [
      Number(dayBoundaries.start.slice(0, 2)),
      Number(dayBoundaries.end.slice(0, 2)),
    ],
    [dayBoundaries],
  );

  // Só na visão de dia: a hora somada dos sete dias dilui até virar cinza (o
  // sábado às 16h em 78% vira 13% na semana), e uma faixa colorida ao lado de
  // uma linha que atravessa sete colunas se lê como "16h está cheio" — o dono
  // olharia segunda às 16h, veria vazio, e concluiria que o painel mente.
  // Semana responde "que DIA está pesado" (a régua horizontal); dia responde
  // "que HORÁRIO está crítico". "Em que horário meu salão enche?" é pergunta de
  // relatório sobre semanas, não de uma semana só.
  const faixasPorHora = useMemo(
    () =>
      view !== "day"
        ? []
        : calcularDemandaPorHora({
            inicio: range.start,
            fim: range.end,
            timeZone,
            appointments,
            professionals: scopedProfessionals,
            businessHours,
            waitlist: waitlistReq.data ?? [],
            abreHora,
            fechaHora,
          }),
    [
      view,
      range,
      timeZone,
      appointments,
      scopedProfessionals,
      businessHours,
      waitlistReq.data,
      abreHora,
      fechaHora,
    ],
  );

  function shift(days: number) {
    const base = range?.start ?? today;
    const next = Temporal.PlainDate.from(base).add({ days });
    calendarControls.setDate(next);
  }

  function changeView(v: "week" | "day") {
    setView(v);
    calendarControls.setView(v);
  }

  const title = useMemo(
    () => tituloDoPeriodo(range?.start ?? today, range?.end ?? today, view, timeZone),
    [range, today, view, timeZone],
  );
  // "Sáb, 5 set" no lugar de "5 de setembro de 2026": por extenso o rótulo
  // quebrava em DUAS linhas ao lado dos controles num celular de 390px, e
  // sozinho empurrava a grade quase 60px pra baixo. O dia da semana entra no
  // lugar do ano porque na visão de dia é ele que se procura.
  const titleShort = useMemo(
    () =>
      tituloDoPeriodo(range?.start ?? today, range?.end ?? today, view, timeZone, true),
    [range, today, view, timeZone],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar — o header nativo do Schedule-X fica escondido (ver
          styles.css); a navegação é toda daqui via calendar-controls. */}
      {/*
       * DUAS faixas no celular, não três. Antes: (1) Hoje + setas + data por
       * extenso, que quebrava em duas linhas; (2) o filtro de profissional em
       * largura cheia; (3) Dia/Semana + Novo Agendamento. Somadas, ~330px de
       * cromo antes do primeiro horário da grade, numa tela de 844.
       *
       * Agora a primeira faixa é a NAVEGAÇÃO (Hoje, setas, data curta) e a
       * segunda são os FILTROS (profissional ocupando o espaço que sobra,
       * Dia/Semana, e o botão de criar com rótulo curto) — as duas em uma
       * linha cada, sem quebra.
       */}
      <div className="flex flex-col gap-2 border-b bg-card px-4 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-6 sm:py-3">
        <div className="flex items-center gap-1 sm:gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => calendarControls.setDate(Temporal.PlainDate.from(today))}
          >
            Hoje
          </Button>
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 sm:h-11 sm:w-11 lg:h-8 lg:w-8"
              aria-label="Período anterior"
              onClick={() => shift(view === "week" ? -7 : -1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 sm:h-11 sm:w-11 lg:h-8 lg:w-8"
              aria-label="Próximo período"
              onClick={() => shift(view === "week" ? 7 : 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {/* Um `h2` só, com o texto trocado por breakpoint: dois elementos
              fariam o leitor de tela anunciar o período duas vezes. */}
          <h2 className="min-w-0 truncate text-base font-semibold sm:text-lg">
            <span className="sm:hidden">{titleShort}</span>
            <span className="hidden sm:inline">{title}</span>
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
          <Select value={professionalFilter} onValueChange={setProfessionalFilter}>
            {/* `min-w-0 flex-1` no celular: o filtro fica com a sobra da linha
                em vez de exigir uma linha só pra ele. */}
            <SelectTrigger
              size="sm"
              className="min-w-0 flex-1 sm:w-52 sm:flex-none"
              aria-label="Filtrar por profissional"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os profissionais</SelectItem>
              {/* Inativo continua na lista — ele tem histórico, e filtrar por
                  ele é a única forma de revê-lo — mas marcado: sem isso o dono
                  escolhia um nome que nunca mais recebe agendamento novo e via
                  uma agenda vazia sem entender por quê. */}
              {(professionals ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.active ? p.name : `${p.name} (inativo)`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => v && changeView(v as "week" | "day")}
            variant="outline"
            size="sm"
            className="shrink-0"
          >
            <ToggleGroupItem value="day">Dia</ToggleGroupItem>
            <ToggleGroupItem value="week">Semana</ToggleGroupItem>
          </ToggleGroup>
          {/* Só o "+" no celular. O rótulo por extenso custava ~55px da linha,
              e quem pagava era o filtro de profissional ao lado, que ficava em
              "Todos os prof…". `sr-only`/`not-sr-only` mantém o nome pro leitor
              de tela e o devolve visualmente a partir do `sm`. */}
          <NovoAgendamentoDialog
            triggerLabel={
              <span className="sr-only sm:not-sr-only">Novo Agendamento</span>
            }
          />
          <NovoAgendamentoDialog defaultEncaixe triggerLabel="Encaixe" />
        </div>
      </div>

      {/* Instância controlada, sem trigger próprio: aberta pelo clique num
          horário vazio da grade. */}
      <NovoAgendamentoDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultDate={slotPrefill?.date}
        defaultTime={slotPrefill?.time}
      />

      <TermometroDemanda
        inicio={range.start}
        fim={range.end}
        timeZone={timeZone}
        appointments={appointments}
        professionals={scopedProfessionals}
        businessHours={businessHours}
        waitlist={waitlistReq.data ?? []}
        calhaPx={calhaPx}
        pico={picoDe(faixasPorHora)}
        establishmentId={establishmentId}
      />

      {/* Ficha do agendamento: frame nosso, não o popover da lib (ver
          AgendamentoEventoContent). */}
      <ResponsiveDialog
        desktop="sheet"
        open={openAppointment !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setOpenId(null);
        }}
      >
        {/*
         * No desktop, painel lateral e não modal: pra decidir um novo horário
         * é preciso VER a agenda, e o modal cobria justamente ela. No celular
         * não existe "ver a agenda atrás" — vira drawer de baixo, que fecha
         * arrastando e com o voltar do Android.
         */}
        <ResponsiveDialogContent className="w-full sm:max-w-md">
          {openAppointment && (
            <AgendamentoEventoContent
              appointment={openAppointment}
              customerName={customerNames.get(openAppointment.customer_id)}
              professionalName={professionalNames.get(openAppointment.professional_id)}
              timeZone={timeZone}
              onClose={() => setOpenId(null)}
            />
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* Calendar */}
      <div ref={gradeRef} className="agenda-schedule-x min-h-0 flex-1">
        {appointmentsReq.isError ? (
          <p role="alert" className="p-6 text-sm text-destructive">
            Não foi possível carregar a agenda. Tente recarregar a página.
          </p>
        ) : (
          <>
            <ScheduleXCalendar calendarApp={calendar} />
            <SlotHover
              gradeRef={gradeRef}
              businessHours={businessHours}
              scheduleExceptions={scheduleExceptions}
              dayBoundaries={dayBoundaries}
            />
            {view === "day" && (
              <TermometroVertical
                faixas={faixasPorHora}
                abreHora={abreHora}
                fechaHora={fechaHora}
                alvo={eixoHoras}
                establishmentId={establishmentId}
              />
            )}
          </>
        )}
      </div>

      {/*
       * Legenda das DUAS dimensões da grade. A cor de preenchimento diz de
       * quem é o horário; a barra da esquerda diz em que estado ele está.
       * Confirmado não tem barra própria de propósito — ele usa a cor do
       * profissional, porque estado normal não precisa de destaque.
       *
       * Só no desktop, onde ela cabe numa linha. No celular os mesmos itens
       * quebram em três e comem a altura da grade, que é o recurso mais
       * escasso da tela — e a informação não se perde: tocar no horário abre
       * a ficha, que diz o profissional e o estado por extenso.
       */}
      <div className="hidden flex-wrap items-center gap-x-5 gap-y-2 border-t bg-card px-6 py-2.5 text-xs text-muted-foreground md:flex">
        {scopedProfessionals.map((prof) => (
          <span key={prof.id} className="flex items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-sm"
              style={{
                backgroundColor: `color-mix(in oklab, ${PROFESSIONAL_HUES[hueIndexFor(prof.id)]} 45%, transparent)`,
              }}
            />
            {prof.name}
          </span>
        ))}
        {scopedProfessionals.length > 0 && (
          <span aria-hidden="true" className="h-4 w-px bg-border" />
        )}
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-amber-500/20 [border-left:3px_solid_var(--color-amber-500)]" />
          Aguardando confirmação
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-muted ring-1 ring-inset ring-foreground/10" />
          Concluído
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-destructive/15 [border-left:3px_solid_var(--destructive)]" />
          Faltou
        </span>
      </div>
    </div>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Rótulo do período mostrado na toolbar, a partir da janela que o calendário
 * reporta (`YYYY-MM-DD`).
 *
 * Semana que atravessa a virada do mês precisa dizer os DOIS: com só o mês do
 * primeiro dia, a semana de 31/08 a 06/09 aparecia como "Agosto de 2026" —
 * seis dos sete dias eram setembro.
 *
 * Exportada por isso: é a única parte da agenda que dá pra testar sem montar o
 * calendário inteiro, e é onde o erro passava despercebido (a tela continua
 * funcionando, só mente o mês).
 */
export function tituloDoPeriodo(
  inicio: string,
  fim: string,
  view: "week" | "day",
  timeZone: string,
  /**
   * Versão curta, pro cabeçalho da agenda no celular: "Sáb, 5 set" em vez de
   * "5 de setembro de 2026", e "Set 2026" em vez de "Setembro de 2026". O ano
   * sai da visão de dia porque o dia da semana informa mais na hora de
   * navegar, e ele continua no rótulo da semana.
   */
  compacto = false,
) {
  const fmt = (dia: string, opcoes: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("pt-BR", { ...opcoes, timeZone }).format(
      new Date(`${dia}T12:00:00Z`),
    );
  if (view === "day") {
    if (compacto) {
      return capitalize(
        fmt(inicio, { weekday: "short", day: "numeric", month: "short" }).replace(
          /\.,/,
          ",",
        ),
      );
    }
    return capitalize(fmt(inicio, { day: "numeric", month: "long", year: "numeric" }));
  }
  if (inicio.slice(0, 7) === fim.slice(0, 7)) {
    return capitalize(
      fmt(inicio, { month: compacto ? "short" : "long", year: "numeric" }),
    );
  }
  return `${capitalize(fmt(inicio, { day: "numeric", month: "short" }))} – ${fmt(fim, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}
